import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Calendar, User, ArrowRight, Search, Tag, Clock, TrendingUp, Zap, BookOpen, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import BlogImage from '../components/ui/BlogImage';
import NewsletterSubscription from '../components/newsletter/NewsletterSubscription';
import { comprehensiveBlogPosts } from '../data/blogContent';

const blogPosts = comprehensiveBlogPosts.length > 0 ? comprehensiveBlogPosts : [
    {
      id: 1,
      title: 'Unlocking the Power of AI for Your Social Media Content',
      excerpt: 'Stop guessing what content will perform well. Learn how Quixocial\'s AI engine can analyze your brand and generate stunning, high-engagement posts, giving you more time to focus on strategy.',
      author: 'The Quixocial Team',
      date: 'September 26, 2025',
      category: 'AI & Technology',
      readTime: '6 min read',
      image: '/images/blog/ai-unlock.svg',
      featured: true,
      content: `
        <p class="text-lg text-gray-700 mb-6">In today\'s fast-paced digital world, staying relevant on social media requires a constant stream of fresh, engaging content. But who has the time to brainstorm, create, and design posts every single day? This is where Artificial Intelligence is changing the game for marketers, agencies, and business owners.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Challenge of Modern Content Creation</h2>
        <p class="text-lg text-gray-700 mb-6">The demand for high-quality content is higher than ever. Your audience expects visually appealing graphics, witty captions, and posts that resonate with them on a personal level. Meeting these expectations consistently across multiple platforms can lead to creative burnout and stretched resources.</p>
        <blockquote class="border-l-4 border-blue-500 pl-4 py-2 my-6">
          <p class="text-xl italic text-gray-800">“The biggest challenge for marketers is not a lack of ideas, but a lack of time to execute them.”</p>
        </blockquote>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">How AI Transforms Your Workflow</h2>
        <p class="text-lg text-gray-700 mb-6">Instead of starting from a blank page, imagine having a creative partner that understands your brand. Quixocial\'s AI engine does just that. By analyzing your brand kit, target audience, and performance goals, our AI can:</p>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li>Generate dozens of unique post ideas in seconds.</li>
          <li>Create stunning visuals and ad creatives tailored to your brand identity.</li>
          <li>Write compelling captions that drive engagement and conversions.</li>
          <li>Suggest the best times to post for maximum reach.</li>
        </ul>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">More Than Just Automation</h2>
        <p class="text-lg text-gray-700 mb-6">This isn\'t about replacing human creativity; it\'s about augmenting it. AI handles the repetitive, time-consuming tasks, freeing you up to focus on what truly matters: building your brand, engaging with your community, and driving business results. It\'s time to stop guessing and start creating with intelligence.</p>
      `
    },
    {
      id: 2,
      title: '5 Time-Saving Tips for Managing Multiple Client Accounts',
      excerpt: 'Juggling multiple social media clients can be chaotic. Discover five practical tips for streamlining your workflow, from centralized content libraries to automated reporting, all within Quixocial.',
      author: 'The Quixocial Team',
      date: 'September 22, 2025',
      category: 'Social Media Tips',
      readTime: '8 min read',
      image: '/images/blog/agency-tips.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">As a social media manager for an agency, your day is a constant balancing act. Switching between different client accounts, brand voices, and content calendars can be overwhelming. But with the right tools and strategies, you can turn chaos into a streamlined, efficient workflow.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Centralize Everything with a Multi-Client Dashboard</h2>
        <p class="text-lg text-gray-700 mb-6">Stop logging in and out of different accounts. A unified dashboard, like the one in Quixocial, allows you to view all your clients in one place. This gives you a high-level overview of all your accounts at a glance.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Use a Shared Media Library</h2>
        <p class="text-lg text-gray-700 mb-6">Don\'t waste time hunting for assets. A centralized media library for each client ensures that all approved logos, images, and videos are in one easy-to-access location. This also helps in maintaining brand consistency.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Master the Art of Bulk Scheduling</h2>
        <p class="text-lg text-gray-700 mb-6">Dedicate specific blocks of time to schedule content for the upcoming week or month. Using a bulk scheduling tool, you can upload and schedule hundreds of posts at once, saving you countless hours.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Create and Reuse Content Templates</h2>
        <p class="text-lg text-gray-700 mb-6">Many social media posts follow a similar format. Create templates for different types of content (e.g., testimonials, promotions, blog post announcements) to speed up the creation process.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Automate Your Reporting</h2>
        <p class="text-lg text-gray-700 mb-6">Manually creating reports for each client is a huge time sink. Use a tool that automatically generates and sends professional, white-labeled reports to your clients, showcasing the amazing results you\'re delivering.</p>
      `
    },
    {
      id: 3,
      title: 'From 2D to 3D: The Future of E-commerce is Here',
      excerpt: 'Product photos are no longer enough. Explore how Quixocial\'s new feature converts your 2D product images into interactive 3D models for AR, and why this is a game-changer for online stores.',
      author: 'The Quixocial Team',
      date: 'September 18, 2025',
      category: 'Product Updates',
      readTime: '5 min read',
      image: '/images/blog/2d-to-3d.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">For years, high-quality product photography has been the cornerstone of e-commerce. But in an increasingly immersive digital world, static images are starting to feel... well, flat. The next frontier in online shopping is Augmented Reality (AR), and it\'s more accessible than you think.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Introducing 2D-to-3D Conversion</h2>
        <p class="text-lg text-gray-700 mb-6">We\'re thrilled to announce a groundbreaking new feature in Quixocial: the ability to convert your existing 2D product photos into fully interactive 3D models. With just a few clicks, you can create an AR experience that allows customers to view your products from any angle, right in their own space.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Why This is a Game-Changer for E-commerce</h2>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li><strong>Increased Conversion Rates:</strong> Studies show that AR experiences can increase conversion rates by up to 94% as customers feel more confident in their purchase.</li>
          <li><strong>Reduced Returns:</strong> By allowing customers to visualize products in their own environment, you reduce the likelihood of returns due to size or style issues.</li>
          <li><strong>Enhanced Engagement:</strong> Interactive 3D content is far more engaging than static images, leading to more shares and a stronger brand connection.</li>
        </ul>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">How It Works</h2>
        <p class="text-lg text-gray-700 mb-6">Our AI-powered tool analyzes your product photos and intelligently constructs a 3D model. You can then share this model as a link on your social media, embed it on your website, or use it to create unique AR ad campaigns. The future of e-commerce is here, and it\'s three-dimensional.</p>
      `
    },
    {
      id: 4,
      title: 'Beyond Likes: Choosing the Right Social Media Analytics to Track',
      excerpt: 'Vanity metrics can be misleading. This guide breaks down the key performance indicators (KPIs) you should be tracking to measure real ROI and prove your social media impact to clients or stakeholders.',
      author: 'The Quixocial Team',
      date: 'September 15, 2025',
      category: 'Marketing Strategy',
      readTime: '7 min read',
      image: '/images/blog/analytics-kpis.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">Likes and follower counts are easy to track, but they don\'t tell the whole story. To truly understand your social media performance and prove its value, you need to look beyond these "vanity metrics" and focus on the key performance indicators (KPIs) that align with your business goals.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Engagement Rate</h2>
        <p class="text-lg text-gray-700 mb-6">This is the percentage of your audience that interacts with your content. It\'s a much better indicator of a healthy, interested audience than just follower count. Track not just likes, but also comments, shares, and saves.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Reach vs. Impressions</h2>
        <p class="text-lg text-gray-700 mb-6"><strong>Impressions</strong> are the total number of times your content was displayed. <strong>Reach</strong> is the number of unique people who saw your content. A high impression count with low reach might indicate your content is being shown to the same people repeatedly.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Click-Through Rate (CTR)</h2>
        <p class="text-lg text-gray-700 mb-6">If your goal is to drive traffic to your website, CTR is crucial. It measures the percentage of people who clicked the link in your post. A low CTR might mean your call-to-action needs to be stronger.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Conversion Rate</h2>
        <p class="text-lg text-gray-700 mb-6">This is the ultimate measure of ROI. It tracks the percentage of users who take a desired action after clicking your link, such as making a purchase, signing up for a newsletter, or filling out a contact form.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Focus on What Matters</h2>
        <p class="text-lg text-gray-700 mb-6">By focusing on these more meaningful metrics, you can make smarter decisions about your content strategy and clearly demonstrate the tangible business impact of your social media efforts. Quixocial\'s analytics dashboard makes it easy to track these KPIs and generate insightful reports.</p>
      `
    },
    {
      id: 5,
      title: 'The Psychology of Social Media Engagement: What Really Drives Clicks',
      excerpt: 'Understanding the psychological triggers behind social media engagement can transform your content strategy. Discover the science-backed principles that make content irresistible to your audience.',
      author: 'The Quixocial Team, Behavioral Psychologist',
      date: 'September 12, 2025',
      category: 'Marketing Strategy',
      readTime: '9 min read',
      image: '/images/blog/psychology-engagement.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">What makes someone stop scrolling and engage with your content? The answer lies in understanding the psychological principles that drive human behavior on social media platforms.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Power of Social Proof</h2>
        <p class="text-lg text-gray-700 mb-6">Humans are inherently social creatures. We look to others for cues about what's valuable, interesting, or worth our attention. This is why engagement metrics like likes, shares, and comments are so powerful.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Curiosity Gap</h2>
        <p class="text-lg text-gray-700 mb-6">Creating a "curiosity gap" in your content is one of the most effective ways to drive engagement. This involves presenting just enough information to pique interest while leaving something to be discovered.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Emotional Triggers That Work</h2>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li><strong>Surprise:</strong> Unexpected content breaks through the noise of predictable feeds.</li>
          <li><strong>Joy:</strong> Positive emotions are highly shareable and create brand affinity.</li>
          <li><strong>Fear of Missing Out (FOMO):</strong> Limited-time offers and exclusive content drive immediate action.</li>
          <li><strong>Belonging:</strong> Content that makes people feel part of a community or movement.</li>
        </ul>
      `
    },
    {
      id: 6,
      title: 'Building a Content Calendar That Actually Works: A Data-Driven Approach',
      excerpt: 'Stop posting randomly and start planning strategically. Learn how to create a content calendar based on audience behavior data, platform algorithms, and business objectives.',
      author: 'Marcus Rodriguez, Content Strategist',
      date: 'September 8, 2025',
      category: 'Social Media Tips',
      readTime: '11 min read',
      image: '/images/blog/content-calendar.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">A well-planned content calendar is the backbone of successful social media marketing. But most businesses approach it wrong, focusing on quantity over quality and ignoring crucial data insights.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Start with Audience Analysis</h2>
        <p class="text-lg text-gray-700 mb-6">Before you plan a single post, you need to understand when your audience is most active, what type of content they engage with, and how they behave across different platforms.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The 80/20 Content Rule</h2>
        <p class="text-lg text-gray-700 mb-6">Follow the 80/20 rule: 80% of your content should provide value (educate, entertain, inspire), while only 20% should be promotional. This builds trust and keeps your audience engaged.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Platform-Specific Optimization</h2>
        <p class="text-lg text-gray-700 mb-6">Each platform has its own optimal posting frequency, content format, and engagement patterns. Your calendar should reflect these differences rather than using a one-size-fits-all approach.</p>
      `
    }
  ];

const BlogPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'AI & Technology', 'Social Media Tips', 'Marketing Strategy', 'Product Updates'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);
  const firstRegularPost = regularPosts.length > 0 ? regularPosts[0] : null;
  const otherRegularPosts = regularPosts.slice(1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white font-sans">
      {/* Modern Header */}
      <header className="relative bg-black/50 backdrop-blur-xl border-b border-gray-700/50 shadow-2xl sticky top-0 z-50">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/" className="flex items-center">
              <div className="h-10 w-auto rounded-lg bg-white/10 backdrop-blur-sm overflow-hidden">
                <img
                  src="/logo-light.png"
                  alt="Quixocial"
                  className="h-full w-full object-contain"
                />
              </div>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-pink-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-blue-500/20 rounded-full blur-xl animate-pulse delay-2000"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="secondary" className="mb-6 px-6 py-3 text-sm font-bold bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-purple-500/30 text-black">
              <BookOpen className="w-4 h-4 mr-2" />
              Insights & Innovation
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-bold mb-10 pb-4 leading-tight">
              <span className="bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">The Quixocial</span>
              <span className="block text-pink-400">
                Knowledge Hub
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Discover the latest insights, strategies, and innovations in social media marketing, AI-powered content creation, and digital transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {featuredPost && (
        <section className="relative mb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700/50 shadow-2xl"
            >
              <div className="absolute inset-0">
                <div className="w-full h-full bg-gradient-to-br from-purple-900/80 via-blue-900/60 to-pink-900/40"></div>
                <div className="absolute inset-0 bg-black/30"></div>
              </div>
              <div className="relative p-8 lg:p-12">
                <div className="max-w-2xl">
                  <Badge variant="secondary" className="mb-6 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {featuredPost.category}
                  </Badge>
                  <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white leading-tight">
                    {featuredPost.title}
                  </h2>
                  <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center gap-6 mb-8 text-gray-300">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {featuredPost.date}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <Link to={`/blog/${featuredPost.id}`}>
                    <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-0 px-8 py-4 text-lg shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
                      <Zap className="w-5 h-5 mr-2" />
                      Read Full Article
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <main className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1 order-2 lg:order-1">
              <div className="sticky top-32 space-y-8">
                {/* Search */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50"
                >
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Search className="w-5 h-5 mr-2 text-purple-400" />
                    Search Articles
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search insights..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 bg-gray-900/50 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500 focus:ring-purple-500/20"
                    />
                  </div>
                </motion.div>
                
                {/* Categories */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50"
                >
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-pink-400" />
                    Categories
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                        className={selectedCategory === category 
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 hover:from-purple-700 hover:to-pink-700' 
                          : 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                </motion.div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-12"
              >
                <h2 className="text-4xl font-bold text-white mb-3 flex items-center">
                  <BookOpen className="w-8 h-8 mr-3 text-purple-400" />
                  Latest Insights
                </h2>
                <p className="text-gray-400 text-lg">Stay ahead with cutting-edge strategies and expert insights</p>
              </motion.div>

              <div className="grid grid-cols-1 gap-8">
                {regularPosts.map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                  >
                    <Link to={`/blog/${post.id}`} className="group block">
                      <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:bg-gray-800/70 overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-5">
                          <div className="md:col-span-2 relative overflow-hidden">
                            <BlogImage 
                              src={post.image} 
                              alt={post.title}
                              title={post.title}
                              category={post.category}
                              className="w-full h-64 md:h-full transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          </div>
                          <div className="md:col-span-3">
                            <CardContent className="p-8">
                              <Badge variant="outline" className="mb-4 border-purple-500/30 text-purple-300 bg-purple-500/10">
                                {post.category}
                              </Badge>
                              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors duration-300 leading-tight">
                                {post.title}
                              </h3>
                              <p className="text-gray-300 mb-6 leading-relaxed line-clamp-3">
                                {post.excerpt}
                              </p>
                              <div className="flex items-center gap-6 text-sm text-gray-400">
                                <div className="flex items-center">
                                  <User className="w-4 h-4 mr-2" />
                                  {post.author}
                                </div>
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-2" />
                                  {post.date}
                                </div>
                                <div className="flex items-center">
                                  <Clock className="w-4 h-4 mr-2" />
                                  {post.readTime}
                                </div>
                              </div>
                            </CardContent>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Newsletter Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-purple-500/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-500/10 rounded-full blur-xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-12 border border-gray-700/50"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Stay Ahead of the Curve
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get exclusive insights, cutting-edge strategies, and the latest AI innovations delivered straight to your inbox. Join thousands of marketers who trust our expertise.
            </p>
            <NewsletterSubscription />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;